import { useParams } from "react-router";
import { useMemo } from "react";
import { AhuName } from "@/services/backend/htz/ahu/components/ahu-name";
import t from "@/lang/lang";
import { AhuDataLabelName } from "@/services/backend/htz/ahu/components/ahu-data-label";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import {
  useHtzAhuComponentCatalogShowActiveCatalogQuery,
  useHtzAhuComponentCatalogShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-component-catalog-api";
import { AirHandlingUnitData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { AhuDataOwnerTypes } from "@/services/backend/htz/ahu/types/ahu-data-owner-types";
import { AhuDataEditCard } from "@/services/backend/htz/ahu/components/ahu-data-edit-card";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { H3 } from "@/shared/components/typography";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import { useAhuDataOwner } from "./_components/use-ahu-data-owner";
import { AhuDataOwnerButton } from "./_components/ahu-data-owner-button";

export function CustomerHtzAhuDataRoute() {
  const { customerId, airHandlingUnitId, ahuDataId } = useParams();
  const { data, isLoading, error } = useHtzAirHandlingUnitDataShowQuery({
    id: ahuDataId!,
  });

  const breadcrumbs = useMemo(
    () => [
      {
        to: `/customers/${customerId}/htz/ahus`,
        label: t("HTZ"),
      },
      {
        to: `/customers/${customerId}/htz/ahus`,
        label: t("RLT-Anlagen"),
      },
      {
        to: `/customers/${customerId}/htz/ahus/${airHandlingUnitId}`,
        label: <AhuName airHandlingUnitId={airHandlingUnitId!} />,
      },
      {
        to: `/customers/${customerId}/htz/ahus/${airHandlingUnitId}/data/${ahuDataId}`,
        label: t("Datensätze"),
      },
      {
        to: `/customers/${customerId}/htz/ahus/${airHandlingUnitId}/data/${ahuDataId}`,
        label: <AhuDataLabelName ahuDataId={ahuDataId!} />,
      },
    ],
    [customerId, airHandlingUnitId, ahuDataId],
  );

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs breadcrumbs={breadcrumbs} />
      </CustomerPageMainHeader>
      <CustomerPageMainContent className="gap-2">
        <PersistenceContextProvider>
          <RTKQueryErrorAlert error={error} />
          {isLoading && <CardSkeleton />}
          {data && (
            <AhuDataContent
              key={data.id}
              ahuData={data}
              airHandlingUnitId={airHandlingUnitId!}
            />
          )}
        </PersistenceContextProvider>
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function AhuDataContent({
  ahuData,
  airHandlingUnitId,
}: {
  ahuData: AirHandlingUnitData;
  airHandlingUnitId: string;
}) {
  const {
    owner,
    isLoading: ownerIsLoading,
    error: ownerError,
    notFound: ownerNotFound,
  } = useAhuDataOwner(ahuData);

  const {
    data: activeCatalog,
    isLoading: activeCatalogIsLoading,
    error: activeCatalogError,
  } = useHtzAhuComponentCatalogShowActiveCatalogQuery({
    companyId: ahuData.companyId,
  });
  const noActiveCatalog =
    activeCatalog !== undefined && activeCatalog.catalogId === null;
  const {
    data: catalog,
    isLoading: catalogIsLoading,
    error: catalogError,
  } = useHtzAhuComponentCatalogShowQuery(
    { id: activeCatalog?.catalogId ?? "" },
    { skip: activeCatalog === undefined || activeCatalog.catalogId === null },
  );

  const isLoading =
    ownerIsLoading || activeCatalogIsLoading || catalogIsLoading;
  const error = ownerError || activeCatalogError || catalogError;

  const description =
    ahuData.ownerType === AhuDataOwnerTypes.Report
      ? t(
          "Die Anlagendaten gehören zum Anlagenbericht. Änderungen wirken sich auf kein anderes Dokument aus. Geänderte Komponenten erscheinen im Bericht erst, wenn sie dort mit „Komponenten in den Bericht übernehmen“ übernommen werden.",
        )
      : t(
          "Die Anlagendaten gehören zum Arbeitsschein. Änderungen wirken sich auf kein anderes Dokument aus.",
        );

  return (
    <>
      <div className="flex items-center justify-between">
        <H3>
          <span>{t("Datensatz")}</span>
          <AhuDataLabelName ahuDataId={ahuData.id} />
          <span>{t("zu RLT-Anlage")}</span>
          <AhuName airHandlingUnitId={airHandlingUnitId} />
        </H3>
        <div className="flex gap-2">
          <PersistenceStateUsingContext />
          {owner && <AhuDataOwnerButton owner={owner} />}
        </div>
      </div>
      <RTKQueryErrorAlert error={error} />
      {ownerNotFound && (
        <Alert variant="destructive">
          <AlertDescription>
            {t(
              "Das Dokument, zu dem dieser Datensatz gehört, wurde nicht gefunden. Der Datensatz kann daher nicht bearbeitet werden.",
            )}
          </AlertDescription>
        </Alert>
      )}
      {noActiveCatalog && (
        <Alert>
          <AlertDescription>
            {t(
              "Für das Unternehmen ist kein Komponentenkatalog aktiv. Aktivieren Sie einen Katalog in den Stammdaten, um die Komponenten zu bearbeiten.",
            )}
          </AlertDescription>
        </Alert>
      )}
      {isLoading && <CardSkeleton />}
      {owner && catalog && (
        <AhuDataEditCard
          ahuData={ahuData}
          catalog={catalog}
          disabled={owner.immutable || isImmutable(ahuData)}
          description={description}
        />
      )}
    </>
  );
}
